import { useMemo } from 'react';
import styled from '@emotion/styled';
import { Remirror as RemirrorTypes } from '@trinity/chat';
import { useRemirrorEditor } from '@trinity/chat-components/components/Remirror/BasicMarkupEditor';
import { VideoPlayer } from '@trinity/chat-components/components/VideoPlayer';
import { MiniAudioPlayer } from '../AudioPlayer/MiniAudioPlayer';
import { FitText } from './FitText';


type Props = {
  quote: RemirrorTypes.RootNode;
  insights: RemirrorTypes.RootNode;
  media: {
    clipUrl?: string;
    type: 'video' | 'audio' | null;
    url: string | null;
  };
};

export const WidgetQuoteCardCompact = (props: Props) => {
  const { state: quote } = useRemirrorEditor({ content: props.quote });
  const { state: insights } = useRemirrorEditor({ content: props.insights });

  const url = useMemo(() => {
    return props.media?.clipUrl || props.media?.url || null;
  }, [props.media]);

  const ready = useMemo(() => {
    return {
      audio: props.media?.type === 'audio' && !!url,
      video: props.media?.type === 'video' && !!url,
      empty: !props.media?.type || !url,
    };
  }, [props.media, url]);

  return (
    <Wrap>
      <Main>
        <Text>
          {!isContentEmpty(props.insights) &&
            <Insights>
              {insights.doc.textContent}
            </Insights>
          }

          {ready.empty &&
            <NoMediaQuote>
              <FitText>
                {`"${quote.doc.textContent}"`}
              </FitText>
            </NoMediaQuote>
          }

          {ready.audio &&
            <Media>
              <PlayerSlot>
                <MiniAudioPlayer audio={{ src: url }} />
              </PlayerSlot>
              <Snippet>
                <FitText>
                  {`"${quote.doc.textContent}"`}
                </FitText>
              </Snippet>
            </Media>
          }

          {ready.video &&
            <VideoBox>
              <Video>
                <VideoPlayer
                  autoplay={false}
                  controls
                  style={{ maxHeight: '90px', width: `calc(${90 * (16/9)}px)` }}
                  src={url} />
              </Video>
              <VideoText>
                <FitText>
                  {`"${quote.doc.textContent}"`}
                </FitText>
              </VideoText>
            </VideoBox>
          }
        </Text>
      </Main>
    </Wrap>
  );
};

WidgetQuoteCardCompact.displayName = 'Widget.Quote.Card.Compact';

// Each level grows into the space it's given, down to the quote box (Text's last child), so the card
// fills its page builder cell.
const Wrap = styled.div({
  padding: '5px 10px',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
});

const Main = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '5px',
  flex: 1,
  minHeight: 0,
});

const Text = styled.div({
  fontSize: '15px',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  '& > *:last-child': { flex: 1 },
});

// The quote boxes below are flex columns that can shrink, so FitText inside gets the box's height.
const fillColumn = { display: 'flex', flexDirection: 'column', minHeight: 0 } as const;

const NoMediaQuote = styled.div({
  ...fillColumn,
  padding: '10px',
  borderRadius: '8px',
  border: '1px solid var(--sec-01)',
  boxShadow: '0px 1px 5px rgba(0,0,0,0.12)',
  fontSize: '13px',
  fontStyle: 'italic',
});

const VideoText = styled.div({
  ...fillColumn,
  flex: 1,
  margin: '5px',
  fontSize: '13px',
  fontStyle: 'italic',
});

const Media = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  minHeight: 0,
  padding: '10px 5px',
  borderRadius: '8px',
  border: '1px solid var(--sec-01)',
  boxShadow: '0px 1px 5px rgba(0,0,0,0.12)',
  marginTop: '10px',
});

// The player keeps its natural width (it's `width: 100%` inside, so it would otherwise share the row);
// the quote takes the rest.
const PlayerSlot = styled.div({
  flex: 'none',
});

const Snippet = styled.div({
  ...fillColumn,
  flex: '1 1 0',
  minWidth: 0,
  alignSelf: 'stretch',
  fontSize: '13px',
  fontStyle: 'italic',
});

const Video = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: 90px;
  padding-top: 10px;
`;

const VideoBox = styled.div({
  ...fillColumn,
  gap: '2px',
  padding: '0 10px 10px',
  borderRadius: '8px',
  border: '1px solid var(--sec-01)',
  boxShadow: '0px 1px 5px rgba(0,0,0,0.12)',
});

const Insights = styled.div`
  margin-bottom: 10px;
`;

function isContentEmpty(value: RemirrorTypes.RootNode) {

  if (!value) return true;

  function isParagraphNodeEmpty(node: RemirrorTypes.ParagraphNode) {
    const content = (node.content || []);
    if (!content.length) return true;

    return content.every(isParagraphContentEmpty);
  }

  return (value.content || []).reduce((acc, node) => {
    if (!acc) return acc;

    return isParagraphNodeEmpty(node);
  }, true);
}

function isParagraphContentEmpty(node: RemirrorTypes.TextNode) {

  if (node.type === RemirrorTypes.NodeType.Text) {
    return !node.text.trim().length;
  }

  return false;
}